import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { BASE_PATH } from '../../shared/types';
import { describeLogEntry, type AdminAccount, type AdminLog, type AdminLogEntry, type AdminOverview } from '../../shared/admin';
import { useMe } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';
import { AccountMenu } from '../components/AccountMenu';
import { HelpLink } from '../components/HelpLink';

const number = (value: number) => value.toLocaleString();
const date = (value: string | null) => value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Not recorded yet';
const shortDate = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });

function DeleteAdminAccount({ account, onClose, onDeleted }: { account: AdminAccount; onClose: () => void; onDeleted: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const [confirmation, setConfirmation] = useState('');
  const [block, setBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { dialog.current?.showModal(); cancel.current?.focus(); }, []);
  const remove = async (event: Event) => {
    event.preventDefault();
    if (busy || confirmation.trim().toLowerCase() !== account.email) return;
    setBusy(true); setError('');
    try { await api.del(`/admin/accounts/${encodeURIComponent(account.id)}`, { confirmEmail: confirmation, block }); onDeleted(); }
    catch (error) {
      setError(error instanceof ApiError && error.code === 'protected_admin' ? 'Administrator accounts are protected from deletion here.' : 'Could not delete this account. Please try again.');
      setBusy(false);
    }
  };
  return <dialog class="event-delete-dialog admin-delete-dialog" ref={dialog} aria-labelledby="admin-delete-title"
    aria-describedby="admin-delete-description" onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}>
    <h2 id="admin-delete-title">Delete this account?</h2>
    <p id="admin-delete-description">This permanently removes <strong>{account.email}</strong> and their {number(account.events)} event{account.events === 1 ? '' : 's'}. Registration links stop working and Google access is revoked. Their spreadsheets and signed forms stay in Google Drive.</p>
    <form onSubmit={remove} class="stack">
      <label>Type the account email to confirm
        <input type="email" autoComplete="off" value={confirmation} disabled={busy} onInput={(event) => setConfirmation(event.currentTarget.value)} />
      </label>
      <label class="admin-block-option"><input type="checkbox" checked={block} disabled={busy} onChange={(event) => setBlock(event.currentTarget.checked)} />
        <span>Also block this email from signing up again.</span>
      </label>
      <p class="muted small">{block ? 'The email stays on the blocklist until you unblock it.' : 'They can sign in again later and start a new account.'}</p>
      {error && <p class="alert" role="alert">{error}</p>}
      <div class="event-delete-actions">
        <button type="button" class="secondary" ref={cancel} disabled={busy} onClick={onClose}>Cancel</button>
        <button type="submit" class="danger-solid" disabled={busy || confirmation.trim().toLowerCase() !== account.email}>
          {busy ? 'Deleting…' : block ? 'Delete and block account' : 'Delete account'}
        </button>
      </div>
    </form>
  </dialog>;
}

const time = (value: string) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });

function pretty(details: string) {
  try { return JSON.stringify(JSON.parse(details), null, 2); } catch { return details; }
}

/**
 * An entry as plain text to paste to a coding agent: what failed, when and where, and the saved JSON. The account's
 * email is left out; the JSON keeps its id.
 */
function agentReport(entry: AdminLogEntry) {
  const { origin, title, message } = describeLogEntry(entry);
  return [
    `Gathered In Forms (gatheredinforms.com) error log entry #${entry.id}, from the Admin page's Log tab.`,
    `Logged: ${entry.at}`,
    `Source: ${origin} (${entry.source})${title !== entry.source ? `: ${title}` : ''}`,
    ...(message ? [`Summary: ${message}`] : []),
    ...(origin === 'Browser' ? ['Stack traces point into the minified production build.'] : []),
    '', 'Details:', '```json', pretty(entry.details), '```',
  ].join('\n');
}

/** Failures saved by the Worker, newest first: reports from browsers, server errors and their causes. */
function AdminLogList({ revision, unreviewed, onReviewed }: { revision: number; unreviewed: number; onReviewed: (change: number) => void }) {
  const [entries, setEntries] = useState<AdminLogEntry[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<number | null>(null);
  const [saving, setSaving] = useState<number | 'all' | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const load = async (before?: number) => {
    setLoading(true); setError('');
    try {
      const page = await api.get<AdminLog>(`/admin/log${before ? `?before=${before}` : ''}`);
      setEntries(current => before ? [...current, ...page.entries] : page.entries); setMore(page.more);
    } catch { setError('Could not load the log. Please try again.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [revision]);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  const copy = async (entry: AdminLogEntry) => {
    try { await navigator.clipboard.writeText(agentReport(entry)); setCopied(entry.id); clearTimeout(copyTimer.current); copyTimer.current = setTimeout(() => setCopied(null), 2500); }
    catch { setError('Could not copy this entry. Open its details and copy them from there.'); }
  };
  const review = async (entry: AdminLogEntry, reviewed: boolean) => {
    setSaving(entry.id); setError('');
    try {
      const { reviewedAt } = await api.put<{ reviewedAt: string | null }>(`/admin/log/${entry.id}/reviewed`, { reviewed });
      setEntries(current => current.map(item => item.id === entry.id ? { ...item, reviewedAt } : item));
      if (!entry.reviewedAt !== !reviewedAt) onReviewed(reviewedAt ? -1 : 1);
    } catch { setError(reviewed ? 'Could not mark this entry as reviewed. Please try again.' : 'Could not mark this entry as new. Please try again.'); }
    finally { setSaving(null); }
  };
  const reviewAll = async () => {
    setSaving('all'); setError('');
    try {
      const { reviewed } = await api.put<{ reviewed: number }>('/admin/log/reviewed', { through: entries[0].id });
      const now = new Date().toISOString();
      setEntries(current => current.map(item => item.reviewedAt || item.id > entries[0].id ? item : { ...item, reviewedAt: now }));
      onReviewed(-reviewed);
    } catch { setError('Could not mark the log as reviewed. Please try again.'); }
    finally { setSaving(null); }
  };
  return <div aria-busy={loading}>
    {error && <p class="alert" role="alert">{error}</p>}
    {unreviewed > 0 && entries.length > 0 && <div class="admin-log-bar"><span class="muted small">{number(unreviewed)} new {unreviewed === 1 ? 'entry' : 'entries'}</span>
      <button type="button" class="link" disabled={saving !== null} onClick={() => { void reviewAll(); }}>{saving === 'all' ? 'Marking…' : 'Mark all reviewed'}</button></div>}
    {entries.length ? <ol class="admin-log">{entries.map(entry => {
      const { origin, title, message } = describeLogEntry(entry);
      return <li key={entry.id} class={entry.reviewedAt ? 'reviewed' : ''}>
        <div class="admin-log-head"><span class={`admin-log-origin ${origin.toLowerCase()}`}>{origin}</span><strong>{title}</strong>
          <time class="muted small" dateTime={entry.at}>{time(entry.at)}</time></div>
        {message && <p class="admin-log-message">{message}</p>}
        {entry.email && <p class="muted small">{entry.email}</p>}
        <details><summary class="small">Details</summary><pre>{pretty(entry.details)}</pre></details>
        <div class="admin-log-actions">
          <button type="button" class="link" onClick={() => { void copy(entry); }}>{copied === entry.id ? 'Copied!' : 'Copy for an agent'}</button>
          <span>{entry.reviewedAt && <span class="muted small">Reviewed {shortDate(entry.reviewedAt)}</span>}
            <button type="button" class="link" disabled={saving === entry.id || saving === 'all'} onClick={() => { void review(entry, !entry.reviewedAt); }}>
              {entry.reviewedAt ? 'Mark as new' : 'Mark reviewed'}</button></span>
        </div>
      </li>;
    })}</ol> : !loading && !error && <p class="admin-empty">Nothing has been logged in the last 30 days.</p>}
    {loading && <p class="muted" role="status">Loading the log…</p>}
    <div class="admin-pagination"><span class="muted small">Errors from the last 30 days, newest first.</span>
      {more && <button class="secondary" disabled={loading} onClick={() => { void load(entries[entries.length - 1].id); }}>Show older</button>}</div>
  </div>;
}

export function Admin() {
  const { me } = useMe();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState<'accounts' | 'blocked' | 'log'>(() => new URLSearchParams(location.search).get('tab') === 'log' ? 'log' : 'accounts');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState<AdminAccount | null>(null);
  const [unblocking, setUnblocking] = useState<string | null>(null);
  const restoreFocus = useRef<HTMLButtonElement | null>(null);
  useEffect(() => { if (me === null) route(`${BASE_PATH}/login`, true); }, [me]);
  useEffect(() => {
    if (!me?.isAdmin) return;
    let active = true;
    setLoading(true); setError('');
    api.get<AdminOverview>(`/admin?page=${page}&search=${encodeURIComponent(search)}`).then((data) => {
      if (active) setOverview(data);
    }).catch((error) => {
      if (active) { setOverview(null); setError(error instanceof ApiError && error.status === 403 ? 'Administrator access is no longer available for this account.' : 'Could not load account information. Please try again.'); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [me, page, search, revision]);
  const closeDelete = () => { setDeleting(null); restoreFocus.current?.focus(); };
  const unblock = async (email: string) => {
    setUnblocking(email); setError('');
    try { await api.post('/admin/unblock', { email }); setNotice(`${email} can sign up again.`); setRevision((value) => value + 1); }
    catch { setError('Could not unblock this email. Please try again.'); }
    finally { setUnblocking(null); }
  };
  if (!me) return <main class="container"><p role="status">Loading…</p></main>;
  return <main class="container dashboard admin-page">
    <div class="dashboard-brandbar"><Brand /><div class="brandbar-actions"><HelpLink /><AccountMenu email={me.email} isAdmin={me.isAdmin} /></div></div>
    <a class="back" href={`${BASE_PATH}/dashboard`}>← Your events</a>
    <header class="dashboard-header"><div><p class="eyebrow">Administration</p><h1>Site activity</h1>
      <p class="muted">Account activity and form counts, with participant information kept private.</p></div></header>
    {!me.isAdmin ? <p class="alert" role="alert">This page is only available to site administrators.</p> : <>
      {error && <p class="alert" role="alert">{error} <button class="link" onClick={() => setRevision((value) => value + 1)}>Retry</button></p>}
      {notice && <p class="admin-notice" role="status">{notice}</p>}
      {overview && <>
        <div class="admin-stats" aria-label="Site totals">
          {[[overview.totals.accounts, 'Accounts'], [overview.totals.events, 'Events'], [overview.totals.submissions, 'Participant submissions'], [overview.totals.logins, 'Successful sign-ins']].map(([value, label]) =>
            <div class="admin-stat" key={label}><strong>{number(value as number)}</strong><span>{label}</span></div>)}
        </div>
        <p class="muted small admin-stats-note">Sign-ins and participant submissions counted since {shortDate(overview.trackingStartedAt)}. Earlier activity is not included. Totals cover current accounts and events, including drafts.</p>
      </>}
      <section class="admin-accounts" aria-label="Account management" aria-busy={loading}>
        <div class="admin-toolbar">
          <div class="admin-switch" aria-label="Lists">
            <button type="button" class={`admin-tab${tab === 'accounts' ? ' selected' : ''}`} aria-pressed={tab === 'accounts'} onClick={() => { setTab('accounts'); setPage(0); }}>Accounts</button>
            <button type="button" class={`admin-tab${tab === 'blocked' ? ' selected' : ''}`} aria-pressed={tab === 'blocked'} onClick={() => { setTab('blocked'); setPage(0); }}>Blocked emails{overview ? ` (${number(overview.totals.blocked)})` : ''}</button>
            <button type="button" class={`admin-tab${tab === 'log' ? ' selected' : ''}`} aria-pressed={tab === 'log'} onClick={() => setTab('log')}>Log{overview?.totals.unreviewedLog ? ` (${number(overview.totals.unreviewedLog)} new)` : ''}</button>
          </div>
          {tab !== 'log' && <form class="admin-search" role="search" onSubmit={(event) => { event.preventDefault(); setSearch(draftSearch.trim()); setPage(0); }}>
            <label class="sr-only" for="admin-search">Search email addresses</label>
            <input id="admin-search" type="search" placeholder="Search email addresses" value={draftSearch} onInput={(event) => setDraftSearch(event.currentTarget.value)} />
            <button type="submit" class="secondary">Search</button>
          </form>}
          <button type="button" class="link" disabled={loading} onClick={() => setRevision((value) => value + 1)}>Refresh</button>
        </div>
        {tab === 'log' ? <AdminLogList revision={revision} unreviewed={overview?.totals.unreviewedLog ?? 0} onReviewed={(change) => setOverview(current => current && { ...current, totals: { ...current.totals, unreviewedLog: Math.max(0, current.totals.unreviewedLog + change) } })} /> : <>
        {loading && <p class="muted" role="status">Loading accounts…</p>}
        {overview && !loading && <>
          {tab === 'accounts' ? overview.accounts.length ? <div class="admin-table-wrap"><table class="admin-table">
            <caption class="sr-only">Registered accounts and their activity</caption>
            <thead><tr><th scope="col">Account</th><th scope="col">Events</th><th scope="col">Submissions</th><th scope="col">Sign-ins</th><th scope="col">Last sign-in</th><th scope="col">Actions</th></tr></thead>
            <tbody>{overview.accounts.map((account) => <tr key={account.id}>
              <th scope="row"><span class="admin-email">{account.email}</span><span class="muted small">Registered {shortDate(account.createdAt)}{account.isAdmin ? ' · Administrator' : ''}</span></th>
              <td>{number(account.events)}</td><td>{number(account.submissions)}</td><td>{number(account.loginCount)}</td><td>{date(account.lastLoginAt)}</td>
              <td>{account.isAdmin ? <span class="muted small">Protected</span> : <button class="danger-outline" aria-label={`Delete account ${account.email}`} onClick={(event) => { restoreFocus.current = event.currentTarget; setDeleting(account); }}>Delete…</button>}</td>
            </tr>)}</tbody>
          </table></div> : <p class="admin-empty">{search ? 'No accounts match this email search.' : 'No accounts have registered yet.'}</p>
          : overview.blocked.length ? <ul class="admin-blocked-list">{overview.blocked.map((item) => <li key={item.email}>
            <div><strong>{item.email}</strong><p class="muted small">Blocked {date(item.blockedAt)}</p></div>
            <button class="secondary" disabled={unblocking !== null} onClick={() => { void unblock(item.email); }}>{unblocking === item.email ? 'Unblocking…' : 'Unblock'}</button>
          </li>)}</ul> : <p class="admin-empty">{search ? 'No blocked emails match this search.' : 'No emails are blocked.'}</p>}
          <div class="admin-pagination"><span class="muted small">{number(tab === 'accounts' ? overview.matchingAccounts : overview.matchingBlocked)} {tab === 'accounts' ? 'accounts' : 'blocked emails'}{search ? ' matching your search' : ''} · Page {page + 1}</span>
            <div class="row"><button class="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
              <button class="secondary" disabled={(page + 1) * 25 >= (tab === 'accounts' ? overview.matchingAccounts : overview.matchingBlocked)} onClick={() => setPage(page + 1)}>Next</button></div>
          </div>
        </>}
        </>}
      </section>
      <p class="muted small admin-privacy-note">We keep one sign-in count and the most recent sign-in time per account, and one submission count per event. No browsing history, device tracking, participant details, or signatures are stored for these statistics.</p>
      {deleting && <DeleteAdminAccount account={deleting} onClose={closeDelete} onDeleted={() => { closeDelete(); setNotice('Account deleted. Its files remain in Google Drive.'); setPage(0); setRevision((value) => value + 1); }} />}
    </>}
  </main>;
}
